import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { APP_CONFIG } from '@/config/app-config';

const formSchema = z.object({
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  confirmPassword: z.string().min(6, { message: 'Password must be at least 6 characters' }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>;

const ResetPassword: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [userType, setUserType] = useState<'client' | 'practitioner'>('client');
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    // Get user type from URL params
    const typeParam = searchParams.get('type');
    if (typeParam === 'practitioner') {
      setUserType('practitioner');
    }

    const checkSession = async () => {
      try {
        console.log('Checking for password reset session...');
        console.log('Current URL:', window.location.href);
        console.log('Current domain:', window.location.origin);
        console.log('Production domain:', APP_CONFIG.PRODUCTION_DOMAIN);
        
        // First, check if this is a fresh redirect from email
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        console.log('Hash params:', { accessToken: !!accessToken, refreshToken: !!refreshToken, type });
        
        if (accessToken && refreshToken && type === 'recovery') {
          console.log('Found recovery tokens in URL hash, setting session...');
          
          // Set the session with the tokens from the URL
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (error) {
            console.error('Error setting session from tokens:', error);
            setError('Failed to validate reset link. The link may have expired or already been used.');
            setHasValidSession(false);
          } else if (data.session) {
            console.log('Successfully set session from recovery tokens');
            setHasValidSession(true);
            setError(null);
            
            // Clear the hash from URL for security
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          } else {
            console.log('No session created from tokens');
            setError('Invalid reset link. Please request a new password reset.');
            setHasValidSession(false);
          }
        } else {
          // Check for existing session
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Error getting session:', error);
            setError('Session error. Please request a new password reset link.');
            setHasValidSession(false);
          } else if (session) {
            console.log('Found existing valid session');
            setHasValidSession(true);
            setError(null);
          } else {
            console.log('No valid session found');
            setError('No valid reset session found. Please click the reset link from your email again, or request a new one if the link has expired.');
            setHasValidSession(false);
          }
        }
      } catch (err: any) {
        console.error('Unexpected error checking session:', err);
        setError('Failed to verify reset session. Please try requesting a new password reset link.');
        setHasValidSession(false);
      } finally {
        setCheckingSession(false);
      }
    };

    checkSession();
  }, [searchParams]);

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setError(null);

    try {
      if (!hasValidSession) {
        throw new Error('No valid session for password reset');
      }

      console.log('Updating password...');
      
      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (error) {
        throw new Error(error.message || 'Failed to reset password');
      }

      toast({
        title: 'Password Reset Successful',
        description: 'Your password has been successfully reset. You can now log in with your new password.',
        duration: 8000,
      });
      
      // Sign out after password reset to ensure clean state
      await supabase.auth.signOut();
      
      // Redirect based on user type
      const redirectPath = userType === 'practitioner' ? '/practitioner-auth' : '/client-auth';
      navigate(redirectPath);
    } catch (err: any) {
      setError(err.message);
      toast({
        variant: 'destructive',
        title: 'Password Reset Failed',
        description: err.message,
        duration: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getBackToLoginPath = () => {
    return userType === 'practitioner' ? '/practitioner-auth' : '/client-auth';
  };

  const handleRequestNewLink = () => {
    const forgotPasswordPath = userType === 'practitioner' 
      ? `/forgot-password?type=practitioner` 
      : `/forgot-password?type=client`;
    navigate(forgotPasswordPath);
  };

  // Show loading while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-amber-50">
        <Navbar />
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center px-4 py-8">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-amber-600" />
            <p className="mt-2 text-neutral-600">Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50">
      <Navbar />
      <div className="flex min-h-[calc(100vh-64px)] items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-left mb-8">
            <h1 className="text-2xl font-semibold text-neutral-800">
              Reset Your Password
            </h1>
            <p className="text-neutral-600">
              Please enter a new password for your {userType} account
            </p>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
            {!hasValidSession ? (
              <div className="text-center">
                <h2 className="mb-4 text-xl font-semibold text-red-800">
                  Reset Link Invalid
                </h2>
                
                {error && (
                  <div className="mb-6 rounded-md bg-red-50 p-4 text-red-600">
                    <p className="text-sm font-medium">{error}</p>
                  </div>
                )}

                <div className="mb-6 text-sm text-neutral-600 space-y-2">
                  <p>This could happen because:</p>
                  <ul className="list-disc list-inside text-left space-y-1">
                    <li>The reset link has already been used</li>
                    <li>The link has expired (links expire after 24 hours)</li>
                    <li>The link was clicked multiple times</li>
                  </ul>
                </div>

                <Button
                  onClick={handleRequestNewLink}
                  className="w-full bg-amber-600 hover:bg-amber-700 mb-4"
                >
                  Request New Reset Link
                </Button>

                <div className="text-center text-sm text-neutral-600">
                  Remember your password?{' '}
                  <a 
                    href={getBackToLoginPath()} 
                    className="text-amber-600 hover:text-amber-800 hover:underline"
                  >
                    Back to Login
                  </a>
                </div>
              </div>
            ) : (
              <>
                <h2 className="mb-4 text-xl font-semibold text-amber-800">
                  Create New Password
                </h2>
                <p className="mb-6 text-sm text-neutral-600">
                  Your password must be at least 6 characters long
                </p>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">New Password</label>
                    <div className="relative">
                      <Input
                        {...form.register("password")}
                        type={showPassword ? "text" : "password"}
                        placeholder="New password"
                        className="border-neutral-300 bg-neutral-50 pl-10"
                        disabled={loading}
                      />
                      <span className="absolute left-3 top-2.5 text-neutral-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </span>
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3 top-2.5 text-neutral-500"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                    {form.formState.errors.password && (
                      <p className="text-xs text-red-500">{form.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">Confirm New Password</label>
                    <div className="relative">
                      <Input
                        {...form.register("confirmPassword")}
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        className="border-neutral-300 bg-neutral-50 pl-10"
                        disabled={loading}
                      />
                      <span className="absolute left-3 top-2.5 text-neutral-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </span>
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3 top-2.5 text-neutral-500"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                    {form.formState.errors.confirmPassword && (
                      <p className="text-xs text-red-500">{form.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="mt-2 w-full bg-amber-600 hover:bg-amber-700"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm text-neutral-600">
                  Remember your password?{' '}
                  <a 
                    href={getBackToLoginPath()} 
                    className="text-amber-600 hover:text-amber-800 hover:underline"
                  >
                    Back to Login
                  </a>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-neutral-600">
            For security reasons, reset links can only be used once and expire in 24 hours
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
